/**
 * Course and module CRUD against Supabase.
 *
 * Replaces the YAML-based registry for course metadata.
 * El contenido vive en `module_block` desde D210.
 */

import { getSupabase, isSupabaseEnabled } from '@/lib/supabase'

export type CourseKind = 'subscription' | 'paid' | 'free'
export type PageType = 'video' | 'text' | 'video-text'
export type ModuleStatus = 'draft' | 'published'

export interface DbCourse {
  id: string
  slug: string
  title: string
  shortTitle: string | null
  description: string | null
  kind: CourseKind
  priceCents: number
  currency: string
  coverUrl: string | null
  durationMin: number
  visible: boolean
  sortOrder: number
}

export interface DbModule {
  id: string
  courseId: string
  slug: string
  title: string
  durationMin: number
  pageType: PageType
  status: ModuleStatus
  sortOrder: number
}

function mapCourse(r: Record<string, unknown>): DbCourse {
  return {
    id: r.id as string,
    slug: r.slug as string,
    title: r.title as string,
    shortTitle: (r.short_title as string) ?? null,
    description: (r.description as string) ?? null,
    kind: (r.kind as CourseKind) ?? 'paid',
    priceCents: (r.price_cents as number) ?? 0,
    currency: (r.currency as string) ?? 'EUR',
    coverUrl: (r.cover_url as string) ?? null,
    durationMin: (r.duration_min as number) ?? 0,
    visible: Boolean(r.visible),
    sortOrder: (r.sort_order as number) ?? 0,
  }
}

function mapModule(r: Record<string, unknown>): DbModule {
  return {
    id: r.id as string,
    courseId: r.course_id as string,
    slug: r.slug as string,
    title: r.title as string,
    durationMin: (r.duration_min as number) ?? 0,
    pageType: (r.page_type as PageType) ?? 'text',
    status: (r.status as ModuleStatus) ?? 'draft',
    sortOrder: (r.sort_order as number) ?? 0,
  }
}

// ── Read ──────────────────────────────────────────────────

export async function listCourses(includeHidden = false): Promise<DbCourse[]> {
  if (!isSupabaseEnabled()) return []
  const supabase = getSupabase()
  if (!supabase) return []

  let q = supabase.from('course').select('*').order('sort_order')
  if (!includeHidden) q = q.eq('visible', true)

  const { data, error } = await q
  if (error) {
    console.info('[courseDb] listCourses:', error.message)
    return []
  }
  return (data ?? []).map(mapCourse)
}

export async function getCourseBySlug(slug: string): Promise<DbCourse | null> {
  if (!isSupabaseEnabled()) return null
  const supabase = getSupabase()
  if (!supabase) return null

  const { data, error } = await supabase
    .from('course')
    .select('*')
    .eq('slug', slug)
    .maybeSingle()

  if (error || !data) return null
  return mapCourse(data)
}

export async function listModules(courseId: string, includeDrafts = false): Promise<DbModule[]> {
  if (!isSupabaseEnabled()) return []
  const supabase = getSupabase()
  if (!supabase) return []

  let q = supabase.from('course_module').select('*').eq('course_id', courseId).order('sort_order')
  if (!includeDrafts) q = q.eq('status', 'published')

  const { data, error } = await q
  if (error) {
    console.info('[courseDb] listModules:', error.message)
    return []
  }
  return (data ?? []).map(mapModule)
}

export async function listModulesBySlug(courseSlug: string, includeDrafts = false): Promise<DbModule[]> {
  const course = await getCourseBySlug(courseSlug)
  if (!course) return []
  return listModules(course.id, includeDrafts)
}

// ── Write (admin only) ────────────────────────────────────

export async function createCourse(input: {
  slug: string
  title: string
  shortTitle?: string
  description?: string
  kind: CourseKind
  priceCents?: number
  durationMin?: number
}): Promise<DbCourse> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  // Put new course at the end
  const { data: existing } = await supabase.from('course').select('sort_order').order('sort_order', { ascending: false }).limit(1)
  const nextOrder = existing && existing.length > 0 ? (existing[0].sort_order as number) + 1 : 0

  const { data, error } = await supabase
    .from('course')
    .insert({
      slug: input.slug,
      title: input.title,
      short_title: input.shortTitle ?? null,
      description: input.description ?? null,
      kind: input.kind,
      price_cents: input.priceCents ?? 0,
      duration_min: input.durationMin ?? 0,
      visible: false,
      sort_order: nextOrder,
    })
    .select()
    .maybeSingle()

  if (error) throw new Error(error.message)
  if (!data) throw new Error('El servidor no devolvió el curso creado')
  return mapCourse(data)
}

export async function updateCourse(id: string, patch: Partial<{
  title: string
  shortTitle: string
  description: string
  kind: CourseKind
  priceCents: number
  durationMin: number
  coverUrl: string
  visible: boolean
  sortOrder: number
}>): Promise<void> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  const dbPatch: Record<string, unknown> = {}
  if (patch.title !== undefined) dbPatch.title = patch.title
  if (patch.shortTitle !== undefined) dbPatch.short_title = patch.shortTitle
  if (patch.description !== undefined) dbPatch.description = patch.description
  if (patch.kind !== undefined) dbPatch.kind = patch.kind
  if (patch.priceCents !== undefined) dbPatch.price_cents = patch.priceCents
  if (patch.durationMin !== undefined) dbPatch.duration_min = patch.durationMin
  if (patch.coverUrl !== undefined) dbPatch.cover_url = patch.coverUrl
  if (patch.visible !== undefined) dbPatch.visible = patch.visible
  if (patch.sortOrder !== undefined) dbPatch.sort_order = patch.sortOrder

  // Antes se tragaba el error y se devolvia boolean. El llamador
  // (courseMut, reorderMut en AdminCourseEditorPage) usaba useMutation
  // sin recibir el error: el toast decia "guardado" aunque no lo
  // fuera. Se tira igual que el resto del modulo tras D-447.
  const { error } = await supabase.from('course').update(dbPatch).eq('id', id)
  if (error) throw new Error(error.message)
}

export async function deleteCourse(id: string): Promise<void> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  const { error } = await supabase.from('course').delete().eq('id', id)
  if (error) throw new Error(error.message)
}

export async function createModule(input: {
  courseId: string
  slug: string
  title: string
  durationMin?: number
  pageType?: PageType
}): Promise<DbModule> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  const { data: existing } = await supabase
    .from('course_module')
    .select('sort_order')
    .eq('course_id', input.courseId)
    .order('sort_order', { ascending: false })
    .limit(1)
  const nextOrder = existing && existing.length > 0 ? (existing[0].sort_order as number) + 1 : 0

  const { data, error } = await supabase
    .from('course_module')
    .insert({
      course_id: input.courseId,
      slug: input.slug,
      title: input.title,
      duration_min: input.durationMin ?? 0,
      page_type: input.pageType ?? 'text',
      status: 'draft',
      sort_order: nextOrder,
    })
    .select()
    .maybeSingle()

  if (error) throw new Error(error.message)
  if (!data) throw new Error('El servidor no devolvió el modulo creado')
  return mapModule(data)
}

export async function updateModule(id: string, patch: Partial<{
  title: string
  durationMin: number
  pageType: PageType
  status: ModuleStatus
  sortOrder: number
}>): Promise<void> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  const dbPatch: Record<string, unknown> = {}
  if (patch.title !== undefined) dbPatch.title = patch.title
  if (patch.durationMin !== undefined) dbPatch.duration_min = patch.durationMin
  if (patch.pageType !== undefined) dbPatch.page_type = patch.pageType
  if (patch.status !== undefined) dbPatch.status = patch.status
  if (patch.sortOrder !== undefined) dbPatch.sort_order = patch.sortOrder

  const { error } = await supabase.from('course_module').update(dbPatch).eq('id', id)
  if (error) throw new Error(error.message)
}

export async function deleteModule(id: string): Promise<void> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  const { error } = await supabase.from('course_module').delete().eq('id', id)
  if (error) throw new Error(error.message)
}

/** Reorder modules: pass the full ordered list of module IDs. */
export async function reorderModules(orderedIds: string[]): Promise<void> {
  if (!isSupabaseEnabled()) throw new Error('Sin conexión con la base de datos')
  const supabase = getSupabase()
  if (!supabase) throw new Error('Sin conexión con la base de datos')

  for (let i = 0; i < orderedIds.length; i++) {
    const { error } = await supabase
      .from('course_module')
      .update({ sort_order: i })
      .eq('id', orderedIds[i])
    if (error) throw new Error(error.message)
  }
}

/** Generates a URL-safe slug from a title. */
export function slugify(title: string): string {
  return title
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .slice(0, 60)
}

/**
 * Returns the set of module slugs that already have published content
 * for a given course, so the admin can flag empty modules.
 */
export async function getModulesWithContent(courseSlug: string): Promise<Set<string>> {
  if (!isSupabaseEnabled()) return new Set()
  const supabase = getSupabase()
  if (!supabase) return new Set()

  const { data, error } = await supabase
    .from('module_block')
    .select('module_slug, body_md, video_url')
    .eq('course_slug', courseSlug)
    .eq('lang', 'es')

  if (error || !data) return new Set()

  const filled = new Set<string>()
  for (const row of data) {
    const hasBody = Boolean((row.body_md as string)?.trim())
    const hasVideo = Boolean(row.video_url)
    if (hasBody || hasVideo) filled.add(row.module_slug as string)
  }
  return filled
}
